// src/components/ArgentinaPhoneInput.jsx
// Teléfono argentino con código de área variable (2-4 dígitos)
// Almacena "54" + código + número. Se inicializa correctamente desde valor guardado.

import { useState, useRef, useEffect } from "react";

const font = "Montserrat, sans-serif";

function numDigits(areaLen) {
  if (areaLen === 4) return 6;
  if (areaLen === 3) return 7;
  return 8;
}

// Intenta parsear "54XXXXXXXXXX" separando código y número
// Usa longitud total para inferir el corte: total local = 10 dígitos
function parseStored(value) {
  if (!value) return { area: "", number: "" };
  const digits = value.replace(/\D/g, "");
  const local  = digits.startsWith("54") ? digits.slice(2) : digits;
  if (!local) return { area: "", number: "" };
  if (local.length < 8) return { area: local, number: "" };
  // Total siempre = 10: inferir código por eliminación
  // Si el total local es exactamente 10, código = total - 8 dígitos del número
  if (local.length === 10) {
    // Heurística: CABA = 11 (2d), Neuquén = 299 (3d), etc.
    // Probamos 2d, 3d, 4d y elegimos el más probable
    // Por defecto asumimos el mínimo que deja número válido
    return { area: local.slice(0, 2), number: local.slice(2) }; // 2d default
  }
  if (local.length === 9) return { area: local.slice(0, 2), number: local.slice(2) };
  if (local.length === 11) return { area: local.slice(0, 3), number: local.slice(3) };
  return { area: local.slice(0, 2), number: local.slice(2) };
}

function displayNum(n, maxN) {
  if (!n) return "";
  const mid = maxN >= 8 ? 4 : 4;
  if (n.length <= mid) return n;
  return n.slice(0, mid) + "-" + n.slice(mid);
}

export default function ArgentinaPhoneInput({ value, onChange, disabled = false, style = {} }) {
  const init = parseStored(value);
  const [area,    setArea]    = useState(init.area);
  const [number,  setNumber]  = useState(init.number);
  const [focused, setFocused] = useState(false);
  const prevValue             = useRef(value);
  const numberRef             = useRef(null);

  // Sincronizar cuando el value externo cambia (ej: al cargar datos del servidor)
  useEffect(() => {
    if (value !== prevValue.current) {
      prevValue.current = value;
      // Solo resetear si los campos están vacíos (primer carga)
      if (!area && !number) {
        const p = parseStored(value);
        setArea(p.area);
        setNumber(p.number);
      }
    }
  }, [value]);

  const areaClean   = area.replace(/\D/g, "");
  const numberClean = number.replace(/\D/g, "");
  const maxN        = numDigits(areaClean.length || 2);
  const isComplete  = areaClean.length >= 2 && numberClean.length === maxN;
  const hasContent  = areaClean.length > 0 || numberClean.length > 0;

  const emit = (a, n) => {
    const d = a.replace(/\D/g, "") + n.replace(/\D/g, "");
    onChange(d ? "54" + d : "");
  };

  const handleArea = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 4);
    setArea(val);
    const newMax     = numDigits(val.length || 2);
    const trimmed    = numberClean.slice(0, newMax);
    setNumber(trimmed);
    emit(val, trimmed);
    if (val.length === 4) numberRef.current?.focus();
  };

  const handleNumber = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, maxN);
    setNumber(val);
    emit(areaClean, val);
  };

  const borderColor = hasContent && !isComplete && !focused
    ? "#E7A432"
    : focused ? "#163244"
    : "rgba(0,0,0,0.12)";

  return (
    <div style={style}>
      <div style={{ display: "flex", alignItems: "stretch", borderRadius: 9, overflow: "hidden", border: `1.5px solid ${borderColor}`, boxShadow: focused ? "0 0 0 3px rgba(22,50,68,0.08)" : "none", transition: "all 0.2s", background: disabled ? "#f5f7fa" : "#fff" }}>

        {/* +54 */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 12px", background: "#f5f7fa", borderRight: "1.5px solid rgba(0,0,0,0.08)", fontSize: 14, fontWeight: 700, color: "#163244", fontFamily: font, whiteSpace: "nowrap", flexShrink: 0 }}>
          🇦🇷 +54
        </div>

        {/* Código */}
        <input
          type="tel"
          value={area}
          onChange={handleArea}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={disabled}
          placeholder="Cód."
          maxLength={4}
          style={{ width: 62, padding: "10px 8px", textAlign: "center", fontFamily: font, fontSize: 14, color: "#163244", background: "transparent", border: "none", outline: "none", borderRight: "1.5px solid rgba(0,0,0,0.08)", flexShrink: 0 }}
        />

        <div style={{ display: "flex", alignItems: "center", padding: "0 8px", color: "#aab8c2", fontSize: 18, flexShrink: 0, userSelect: "none" }}>–</div>

        {/* Número */}
        <input
          ref={numberRef}
          type="tel"
          value={displayNum(numberClean, maxN)}
          onChange={handleNumber}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={disabled || areaClean.length < 2}
          placeholder={areaClean.length >= 2 ? `${"X".repeat(maxN).replace(/(.{4})(.*)/, "$1-$2")}` : "Primero el código"}
          style={{ flex: 1, padding: "10px 12px", fontFamily: font, fontSize: 14, color: "#163244", background: "transparent", border: "none", outline: "none" }}
        />

        {isComplete && (
          <div style={{ display: "flex", alignItems: "center", paddingRight: 12, color: "#1b7f4e", fontSize: 16, flexShrink: 0 }}>✓</div>
        )}
      </div>

      <div style={{ marginTop: 5, fontSize: 11, color: "#aab8c2", fontFamily: font, lineHeight: 1.45 }}>
        {areaClean.length === 0
          ? "Código sin el 0 · Ej: 299 (Neuquén), 11 (CABA), 261 (Mendoza), 3854 (Sgo. Estero)"
          : areaClean.length === 1
          ? "Código mínimo 2 dígitos"
          : `Código ${areaClean.length}d → número de ${maxN} dígitos · ${numberClean.length}/${maxN}`
        }
      </div>
    </div>
  );
}
